import { logger } from '../utils/logger';
import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    LinearProgress,
    useTheme,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

interface NexusState {
    state_code: string;
    state_name: string;
    sales_threshold: number;
    current_sales: number;
    transaction_threshold?: number;
    current_transactions: number;
    percent_to_sales_threshold: number;
    percent_to_transaction_threshold?: number;
    exceeded_threshold: boolean;
    approaching_threshold: boolean;
    nexus_status: 'exceeded' | 'approaching' | 'safe';
    effective_date: string;
    lookback_period: string;
    current_period?: string;
}

interface NexusListViewProps {
    nexusData: NexusState[];
    filingPeriod?: string;
    companyId?: number;
}

const NexusListView: React.FC<NexusListViewProps> = ({ nexusData, filingPeriod, companyId: selectedCompanyId }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { companyId: routeCompanyId } = useParams<{ companyId: string }>();
    const companyId = selectedCompanyId ?? routeCompanyId;

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'exceeded':
                return <ErrorIcon color="error" />;
            case 'approaching':
                return <WarningIcon color="warning" />;
            case 'safe':
                return <CheckCircleIcon color="success" />;
            default:
                return undefined;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'exceeded':
                return 'error';
            case 'approaching':
                return 'warning';
            case 'safe':
                return 'success';
            default:
                return 'default';
        }
    };

    const handleStateClick = (stateCode: string) => {
        if (!companyId) return;
        
        const params = new URLSearchParams();
        params.append('state', stateCode);
        if (filingPeriod) {
            params.append('filing_period', filingPeriod);
        }
        
        navigate(`/company/${companyId}/transaction-insights?${params.toString()}`);
    };

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>State</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sales Progress</TableCell>
                        <TableCell>Transaction Progress</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {nexusData.map((state) => (
                        <TableRow 
                            key={state.state_code} 
                            hover 
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleStateClick(state.state_code)}
                        >
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getStatusIcon(state.nexus_status)}
                                    <Box>
                                        <Typography variant="body2" fontWeight="600">
                                            {state.state_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {state.state_code}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={state.nexus_status.toUpperCase()}
                                    color={getStatusColor(state.nexus_status) as any}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <Box sx={{ minWidth: 200 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="caption">
                                            ${state.current_sales.toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption">
                                            ${state.sales_threshold.toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(state.percent_to_sales_threshold, 100)}
                                        color={getStatusColor(state.nexus_status) as any}
                                        sx={{ height: 6, borderRadius: 3 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {state.percent_to_sales_threshold}% of threshold
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                {state.transaction_threshold ? (
                                    <Box sx={{ minWidth: 200 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption">
                                                {state.current_transactions}
                                            </Typography>
                                            <Typography variant="caption">
                                                {state.transaction_threshold}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(state.percent_to_transaction_threshold || 0, 100)}
                                            color={getStatusColor(state.nexus_status) as any}
                                            sx={{ height: 6, borderRadius: 3 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {state.percent_to_transaction_threshold}% of threshold
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        N/A
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {new Date(state.effective_date).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {state.lookback_period}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Tooltip title={`View transaction insights for ${state.state_name}`}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStateClick(state.state_code);
                                        }}
                                        sx={{ color: theme.palette.primary.main }}
                                    >
                                        <AnalyticsIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default NexusListView; 
